import Layout from "@/components/Layout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const FAQ = () => {
  const { getConfig } = useSiteConfig();
  
  const faqs = getConfig('faq_categories', [
    {
      category: "General",
      questions: [
        {
          q: "What services do you offer?",
          a: "We offer premium transportation services including airport transfers, hotel transfers, private tours, group transportation, and corporate services throughout Punta Cana and the Dominican Republic."
        },
        {
          q: "How do I book a service?",
          a: "You can book our services directly through our website by selecting your desired service, filling out the booking form with your details, and completing the payment process. You'll receive a confirmation email with all the details."
        },
        {
          q: "What areas do you serve?",
          a: "We primarily serve Punta Cana, Bavaro, Cap Cana, Uvero Alto, and surrounding areas. We also offer transfers to other major destinations in the Dominican Republic including Santo Domingo, La Romana, and Samana."
        }
      ]
    },
    {
      category: "Booking & Payment",
      questions: [
        {
          q: "How far in advance should I book?",
          a: "We recommend booking at least 24-48 hours in advance to ensure availability, especially during peak season. However, we also accept last-minute bookings based on availability."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept major credit cards, PayPal, and cash payments. Full payment is required at the time of booking to confirm your reservation."
        },
        {
          q: "What is your cancellation policy?",
          a: "Free cancellation is available up to 24 hours before your scheduled service. Cancellations made less than 24 hours in advance may be subject to a cancellation fee."
        },
        {
          q: "Will I receive a confirmation?",
          a: "Yes, you will receive an email confirmation immediately after booking with all the details of your service, including pickup time, location, and driver information."
        }
      ]
    },
    {
      category: "Transportation",
      questions: [
        {
          q: "What types of vehicles do you have?",
          a: "Our fleet includes sedans (up to 3 passengers), SUVs (up to 5 passengers), vans (up to 8 passengers), and coaches (up to 25 passengers). All vehicles are modern, air-conditioned, and well-maintained."
        },
        {
          q: "Are your drivers licensed and insured?",
          a: "Yes, all our drivers are fully licensed, professionally trained, and our vehicles are fully insured for your safety and peace of mind."
        },
        {
          q: "Do drivers speak English?",
          a: "Yes, all our drivers speak English. Many also speak Spanish and other languages to better serve our international clients."
        },
        {
          q: "What if my flight is delayed?",
          a: "We monitor all flight arrivals in real-time. If your flight is delayed, your driver will adjust the pickup time accordingly at no extra charge."
        }
      ]
    },
    {
      category: "Tours",
      questions: [
        {
          q: "What's included in the tour price?",
          a: "Tour packages typically include transportation, professional guide, entrance fees to attractions, and some meals or refreshments. Specific inclusions are listed in each tour description."
        },
        {
          q: "Can I customize a tour?",
          a: "Yes! We offer customizable private tours where you can choose your destinations and activities. Contact us to discuss your preferences."
        },
        {
          q: "Are tours suitable for children?",
          a: "Most of our tours are family-friendly. However, some adventure tours may have age or height restrictions. Check the tour details or contact us for specific information."
        }
      ]
    },
    {
      category: "Contact & Support",
      questions: [
        {
          q: "How can I contact you?",
          a: "You can reach us via phone, email, WhatsApp, or through our website contact form. We're available 24/7 for assistance."
        },
        {
          q: "Do you offer 24/7 support?",
          a: "Yes, we provide 24/7 customer support for emergencies and last-minute bookings. Our team is always ready to assist you."
        },
        {
          q: "What if I have special requirements?",
          a: "Please let us know about any special requirements (child seats, wheelchair accessibility, special requests) when booking, and we'll do our best to accommodate your needs."
        }
      ]
    }
  ]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-primary-foreground/90">
              Find answers to common questions about our services, booking process, and more.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((category, idx) => (
              <Card key={idx} className="p-6">
                <h2 className="text-2xl font-bold mb-6 text-primary">
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((faq, qIdx) => (
                    <AccordionItem key={qIdx} value={`item-${idx}-${qIdx}`}>
                      <AccordionTrigger className="text-left font-semibold">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Still Have Questions?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Can't find the answer you're looking for? Our customer support team is here to help you 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="gap-2" asChild>
                <a href="/contact">
                  <MessageCircle className="h-5 w-5" />
                  Contact Us
                </a>
              </Button>
              <Button size="lg" variant="outline" className="gap-2" asChild>
                <a href="tel:+18091234567">
                  <Phone className="h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
